import {LitElement, html, nothing, PropertyValues} from "lit";
import {customElement, property, state, query, queryAll} from "lit/decorators.js";
import {classMap} from "lit/directives/class-map.js";
import {Placement} from "@floating-ui/dom";

import {selectStyles} from "./Select.styles.js";
import {attachTooltip} from "../tooltipControl.js";

export const SELECT_CLASS_UNFOCUSABLE = "unfocusable"; // List item that does respond to keyboard or mouse events
const CLASS_EXPANDED = "expanded";
const CLASS_OPTION_ACTIVE = "active";
const CLASS_OPTION_SELECTED = "selected";
const CLASS_PLACEHOLDER = "placeholder";

// For accessibility
interface SelectAriaObject {
	name:  "aria-label" | "aria-labelledby";
	value: string; // name of the chosen aria labelling attribute
}

type SelectCallback = (value: string) => any;

type SelectRenderer = (option: SelectOption) => any;

// An option in the select dropdown.
export interface SelectOption {
	value:     string;
	label:     string;
	disabled?: boolean; // assumed false by default

	// Allow any additional properties
	[x: string | number | symbol]: unknown;
};

@customElement("pn-select")
export class Select extends LitElement {
	@query(`.${CLASS_OPTION_ACTIVE}`) activeOptionElem:     HTMLElement | null;
	@query("ul") listElem:                                  HTMLElement;
	@query(".select-wrapper") referenceElem:                HTMLElement;
	@query(".select-box-content") selectBoxContentElem:     HTMLElement;
	@query(".select-box") selectBoxElem:                    HTMLElement;
	@query(`.${CLASS_OPTION_SELECTED}`) selectedOptionElem: HTMLElement | null;
	@queryAll("li") selectOptionsElems:                     HTMLElement[] | null;

	@property({}) ariaLabelAttr:     SelectAriaObject;
	@property({}) id:                string;
	@property({}) lang:              string;
	@property({}) onSelectCallback?: SelectCallback; // executed when an option is selected, passes the selected value
	@property({}) customRenderer?:   SelectRenderer; // renders what goes inside options and the select box

	@property({}) disabled = false;
	@property({}) options:           SelectOption[]; // list of menu options
	@property({}) placeholder?:      SelectOption; // optional placeholder for when nothing is selected
	@property({}) placeholderShown?: boolean; // show placeholder in option list
	@property({}) required = false;
	@property({}) selectedOption:    null | SelectOption;

	@property() tooltipOptions: {
		disabled?:        boolean;
		tooltipPosition?: Placement;
	} = {
			disabled:        true,
			tooltipPosition: "right"
		};

	@state() _isListBoxOpen = false;
	@state() _selectedOption: SelectOption | null;

	static styles = [selectStyles];

	static shadowRootOptions = {...LitElement.shadowRootOptions, delegatesFocus: true};

	connectedCallback() {
		super.connectedCallback();
		document.body.addEventListener("click", this.checkDismiss);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		document.body.removeEventListener("click", this.checkDismiss);
	}

	firstUpdated() {
		if (this.selectedOption)
			this.setSelectedOption();
	}

	updated(changedProperties: PropertyValues) {
		// Add tooltips if they're selected to be shown
		if (changedProperties.has("options") && !this.tooltipOptions?.disabled && this.selectOptionsElems !== null) {
			attachTooltip(this.selectOptionsElems, {
				showOnlyIfOverflowing: true,
				tooltipPosition:       this.tooltipOptions?.tooltipPosition,
				tooltipText:           (elem: HTMLElement) => elem.innerText
			});

			// TODO: show/hide tooltips for keyboard control
		}
	}

	/** Hide options list if click was outside of the select menu **/
	checkDismiss = (event: MouseEvent) => {
		if (!event.composedPath().includes(this.referenceElem) && this._isListBoxOpen)
			this.closeListBox();
	};

	/** Initialization **/
	renderOption = (): SelectRenderer => {
		if (this.customRenderer)
			return this.customRenderer;
		else {
			// Default render
			return (option: SelectOption) => {
				return option.label;
			};
		}
	};

	/** Helper functions **/
	getListId = () => {
		return `${this.id}--list`;
	};

	getListItemId = (option: SelectOption): string => {
		return `${this.id}-${option.value}`;
	};

	getOptionByValue = (value: string): SelectOption | undefined => {
		return this.options.find(option => option.value === value);
	};

	getOptionElementByObject = (optionObject: SelectOption): HTMLElement | null => {
		const id = this.getListItemId(optionObject);
		return this.referenceElem.querySelector(`#${id}`)!;
	};

	renderPlaceholder = () => {
		if (!this.placeholder)
			return;

		this.selectBoxContentElem.innerHTML = this.renderOption()(this.placeholder);
		this.selectBoxContentElem.classList.add(CLASS_PLACEHOLDER);
	};

	/** List box, element that contains the list of options **/
	closeListBox = () => {
		this._isListBoxOpen = false;
		this.selectBoxElem.removeAttribute("aria-activedescendant");
		this.removeActiveVisualFocus();
	};

	openListBox = () => {
		this._isListBoxOpen = true;
	};

	/** Visual focus **/
	visualFocusFirstInList = () => {
		let firstInList = this.listElem.firstElementChild;
		if (firstInList?.classList.contains(SELECT_CLASS_UNFOCUSABLE))
			firstInList = firstInList.nextElementSibling;
		this.setActiveVisualFocus(firstInList);
	};

	visualFocusLastInList = () => {
		let lastInList = this.listElem.lastElementChild;
		if (lastInList?.classList.contains(SELECT_CLASS_UNFOCUSABLE))
			lastInList = lastInList.previousElementSibling;
		this.setActiveVisualFocus(lastInList);
	};

	visualFocusNextInList = () => {
		if (this.activeOptionElem) {
			let nextOption = this.activeOptionElem.nextElementSibling;
			if (nextOption && (nextOption as HTMLElement).classList.contains(SELECT_CLASS_UNFOCUSABLE))
				nextOption = nextOption.nextElementSibling;
			if (nextOption) {
				this.setActiveVisualFocus(nextOption);
				return;
			}
		}
		// If nothing is focused yet, or you're already at the start of the list
		this.visualFocusFirstInList();
	};

	visualFocusPrevInList = () => {
		if (this.activeOptionElem) {
			let prevOption = this.activeOptionElem.previousElementSibling;
			if (prevOption && (prevOption as HTMLElement).classList.contains(SELECT_CLASS_UNFOCUSABLE))
				prevOption = prevOption.previousElementSibling;
			if (prevOption) {
				this.setActiveVisualFocus(prevOption);
				return;
			}
		}
		// If nothing is focused yet, or you're already at the end of the list
		this.visualFocusLastInList();
	};

	removeActiveVisualFocus = () => {
		if (this.activeOptionElem) {
			this.activeOptionElem.setAttribute("aria-selected", "false");
			this.activeOptionElem.classList.remove(CLASS_OPTION_ACTIVE);
		}
	};

	setActiveVisualFocus = (option: Element | null) => {
		if (!option)
			return;

		this.removeActiveVisualFocus();

		option.classList.add(CLASS_OPTION_ACTIVE);
		this.selectBoxElem.setAttribute("aria-activedescendant", option.id);
		option.setAttribute("aria-selected", "true");
	};

	/** Select menu event handlers **/
	onSelectBoxClick = (event: MouseEvent) => {
		event.preventDefault();

		if (this._isListBoxOpen)
			this.closeListBox();
		else {
			this.openListBox();
			this.visualFocusFirstInList();
		}
	};

	onSelectBoxKeydown = (event: KeyboardEvent) => {
		const key = event.key;

		const keys = ["ArrowDown", "ArrowUp", "Home", "End", " ", "Tab", "Enter", "Escape"];
		if (!keys.includes(key))
			return;

		// Allow default behavior of tab key after this function returns
		if (key !== "Tab")
			event.preventDefault();

		if (this._isListBoxOpen) {
			if (key === "ArrowDown")
				this.visualFocusNextInList();
			else if (key === "ArrowUp")
				this.visualFocusPrevInList();
			else if (key === "Home")
				this.visualFocusFirstInList();
			else if (key === "End")
				this.visualFocusLastInList();
			else if (key === " " || key === "Enter" || key === "Tab") {
				this.saveSelectedOptionElem(this.activeOptionElem);
				this.closeListBox();
			} else if (key === "Escape")
				this.closeListBox();
		} else {
			if (key === "ArrowDown" || key === "Home" || key === " " || key === "Enter") {
				this.openListBox();
				this.visualFocusFirstInList();
			} else if (key === "ArrowUp" || key === "End") {
				this.openListBox();
				this.visualFocusLastInList();
			}
		}
	};

	/** Options event handlers **/
	onClickListItem = (event: MouseEvent) => {
		// composedPath is necessary to get the path through custom elements
		const clickTarget = event.composedPath()[0];

		this.saveSelectedOptionElem(clickTarget as HTMLElement);
		this.closeListBox();
	};

	onMouseoverListItem = (event: MouseEvent) => {
		this.setActiveVisualFocus(event.target as Element);
	};

	/** Save new selected option and execute callback if it exists **/
	saveSelectedOptionElem = (optionElem: HTMLElement | null) => {
		const value = optionElem?.getAttribute("data-value");

		if (!value)
			return;

		this.setSelectedOption(value);

		if (this.onSelectCallback)
			this.onSelectCallback(value);
	};

	/**
	 * Public functions
	 **/
	getReferenceElem = (): HTMLElement => {
		return this.referenceElem;
	};

	getSelectedOption = (): SelectOption | null => {
		return this._selectedOption;
	};

	updateOptions = (newOptions: SelectOption[]) => {
		this.options = newOptions;
	};

	// TODO: this function can probably be changed so it's only called when
	// this.selectedOption is changed, and not need to be used outside of this
	// file anymore
	/**
	 * Visually updates content box and selected element.
	 * If the value specified is not in the options list, the content box will
	 * be set to empty (or the placeholder if it exists), and the selected
	 * element will be unset.
	 */
	setSelectedOption = (value?: string) => {
		if (this.selectedOptionElem)
			this.selectedOptionElem.classList.remove(CLASS_OPTION_SELECTED);

		if (value || this.selectedOption) {
			// @ts-ignore
			const option = this.getOptionByValue(value ?? this.selectedOption.value);

			// Option is in the list, add the selected CSS class to it in the list
			if (option) {
				const optionElem = this.getOptionElementByObject(option);
				if (optionElem) {
					this._selectedOption = option;
					optionElem.classList.add(CLASS_OPTION_SELECTED);
					return;
				}
			}
		}
	};

	renderSelectedOption() {
		let renderContent;
		let isPlaceholder = false;

		if (this._selectedOption)
			renderContent = this.getOptionByValue(this._selectedOption.value);
		else if (this.placeholder) {
			renderContent = this.placeholder;
			isPlaceholder = true;
		}

		if (!renderContent)
			renderContent = {label: "", value: ""};

		return html`
			<span
				class="${classMap({
					"select-box-content": true,
					[CLASS_PLACEHOLDER]:  isPlaceholder
				})}"
			>${this.renderOption()(renderContent)}</span>
		`;
	}

	renderOptionsList() {
		const options = [
			// Add placeholder to the top of the list if it should be shown in it
			...((this.placeholder && this.placeholderShown) ? [this.placeholder] : []),
			...this.options
		];

		const optionsElems = options.map(option => {
			const isDisabled = !!option.disabled;
			return html`
				<li
					class="${classMap({
						"disabled":                 isDisabled,
						// Prevent mouse/keyboard actions on this option
						[SELECT_CLASS_UNFOCUSABLE]: isDisabled
					})}"
					data-tippy-content="${isDisabled ? option.label : nothing}"
					data-value="${option.value}"
					id="${this.getListItemId(option)}"
					role="option"
					@click="${isDisabled ? nothing : this.onClickListItem}"
					@mouseover="${isDisabled ? nothing : this.onMouseoverListItem}"
				>${this.renderOption()(option)}</li>
			`;
		});

		return html`
			<ul
				class="custom-scroll-bar-light"
				id="${this.getListId()}"
				part="list"
				role="listbox"
			>
				${optionsElems}
			</ul>
		`;
	}

	render() {
		// TODO:
		// selectBox.setAttribute(ariaLabelAttr.name, ariaLabelAttr.value);
		return html`
			<div
				aria-label="Select menu"
				aria-required="${this.required ? "true" : nothing}"
				class="${classMap({
					"select-wrapper":     true,
					disabled:             this.disabled,
					[CLASS_EXPANDED]:     this._isListBoxOpen,
					"solid-border":       true,
					"solid-border-light": false
				})}"
				id="${this.id}--wrapper"
				lang="${this.lang}"
			>
				<button
					aria-controls="${this.getListId()}"
					aria-expanded="${this._isListBoxOpen ? "true" : "false"}"
					class="select-box"
					@click="${this.onSelectBoxClick}"
					?disabled="${this.disabled}"
					@keydown="${this.onSelectBoxKeydown}"
					type="button"
				>
					${this.renderSelectedOption()}
					<i class="icon-chevron-light-down"></i>
				</button>
				${this.renderOptionsList()}
			</div>
		`;
	}
};

declare global {
	interface HTMLElementTagNameMap {
		"pn-select": Select;
	}
}
